/**
 * Trial Schema Configuration — aligned with Document Authoring Tool_Forms_17June2026.pdf (pages 5–8).
 * UI shows human-readable labels only; `value` is stored in DB; optional `code` is CDISC CT for export/registry.
 */

export const TRIAL_TYPE_OPTIONS = [
  { key: 'interventional', label: 'Interventional' },
  { key: 'observational', label: 'Observational' },
  { key: 'expanded_access', label: 'Expanded Access' },
  { key: 'patient_registry', label: 'Patient Registry' },
];

export const ARM_STRUCTURE_OPTIONS = [
  { value: 'single_arm', label: 'Single Arm' },
  { value: 'multi_arm', label: 'Multi-arm' },
];

export const ALLOCATION_OPTIONS = [
  { value: 'randomisation', label: 'Randomisation', code: 'C25196' },
  { value: 'no_intervention_assignment', label: 'No Intervention Assignment Method', code: 'C222801' },
  { value: 'other', label: 'Other', code: 'C17649' },
];

export const BLINDING_OPTIONS = [
  { value: 'double_blind', label: 'Double Blind', code: 'C15228' },
  { value: 'observer_blind', label: 'Observer Blind', code: 'C187674' },
  { value: 'open_label', label: 'Open Label', code: 'C49659' },
  { value: 'single_blind', label: 'Single Blind', code: 'C28233' },
];

export const CENTRE_CONFIGURATION_OPTIONS = [
  { value: 'single_centre', label: 'Single-Centre', code: 'C217004' },
  { value: 'multi_centre', label: 'Multi-Centre', code: 'C217005' },
];

export const GEOGRAPHIC_DISTRIBUTION_OPTIONS = [
  { value: 'single_country', label: 'Single Country', code: 'C217006' },
  { value: 'multiple_countries', label: 'Multiple Countries', code: 'C217007' },
];

export const MASTER_PROTOCOL_FRAMEWORK_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'platform', label: 'Platform' },
  { value: 'umbrella', label: 'Umbrella' },
  { value: 'basket', label: 'Basket' },
];

export const ADAPTIVE_FEATURES_OPTIONS = [
  { value: 'yes', label: 'Yes', code: 'C49488' },
  { value: 'no', label: 'No', code: 'C49487' },
];

export const PART_PHASE_OPTIONS = [
  'Early Phase 1',
  'Phase 1',
  'Phase 1/Phase 2',
  'Phase 1/Phase 2/Phase 3',
  'Phase 1/Phase 3',
  'Phase 2',
  'Phase 2/Phase 3',
  'Phase 2/Phase 3/Phase 4',
  'Phase 3',
  'Phase 3/Phase 4',
  'Phase 4',
];

export const INTERVENTION_MODEL_OPTIONS = [
  { value: 'single_group', label: 'Single Group', code: 'C82640' },
  { value: 'parallel_group', label: 'Parallel Group', code: 'C82639' },
  { value: 'crossover', label: 'Cross-over', code: 'C82637' },
  { value: 'factorial', label: 'Factorial', code: 'C82638' },
  { value: 'sequential', label: 'Sequential', code: 'C142568' },
  { value: 'other', label: 'Other', code: 'C17649' },
];

export const ARM_TYPE_OPTIONS = [
  { value: 'active_comparator', label: 'Active Comparator Arm', code: 'C174267' },
  { value: 'control', label: 'Control Arm', code: 'C174226' },
  { value: 'experimental', label: 'Experimental Arm', code: 'C174266' },
  { value: 'no_intervention', label: 'No Intervention Arm', code: 'C174270' },
  { value: 'placebo_comparator', label: 'Placebo Comparator Arm', code: 'C174268' },
  { value: 'sham_comparator', label: 'Sham Comparator Arm', code: 'C174269' },
];

/** Resolve CDISC CT code for a trial-schema option (export / registry). */
export const getTrialSchemaOptionCode = (options, value) => {
  if (!value) return null;
  const opt = (options || []).find((o) => o.value === value);
  return opt?.code || null;
};

/** All framework + part-level codes resolved from saved trial_schema_configuration. */
export const resolveTrialSchemaCdiscCodes = (config = {}) => {
  const fw = config.framework || {};
  const codes = {
    allocation: getTrialSchemaOptionCode(ALLOCATION_OPTIONS, fw.allocation),
    blinding: getTrialSchemaOptionCode(BLINDING_OPTIONS, fw.blinding),
    centre_configuration: getTrialSchemaOptionCode(CENTRE_CONFIGURATION_OPTIONS, fw.centre_configuration),
    geographic_distribution: getTrialSchemaOptionCode(
      GEOGRAPHIC_DISTRIBUTION_OPTIONS,
      fw.geographic_distribution
    ),
    adaptive_features: getTrialSchemaOptionCode(ADAPTIVE_FEATURES_OPTIONS, fw.adaptive_features),
  };
  const partCodes = {};
  Object.entries(config.parts || {}).forEach(([partKey, part]) => {
    if (!part || typeof part !== 'object') return;
    partCodes[partKey] = {
      intervention_model: getTrialSchemaOptionCode(INTERVENTION_MODEL_OPTIONS, part.intervention_model),
      cohort_rows: (part.cohort_rows || []).map((row) => ({
        arm_type: getTrialSchemaOptionCode(ARM_TYPE_OPTIONS, row?.arm_type),
      })),
      direct_rows: (part.direct_rows || []).map((row) => ({
        arm_type: getTrialSchemaOptionCode(ARM_TYPE_OPTIONS, row?.arm_type),
      })),
    };
  });
  return { framework: codes, parts: partCodes };
};

export const emptyCohortRow = () => ({
  cohort_name: '',
  arm_type: '',
  regimen: '',
  dose_levels_frequency: '',
  agent_names: '',
  planned_enrollment: '',
});

export const emptyDirectRow = () => ({
  arm_type: '',
  regimen: '',
  dose_levels_frequency: '',
  agent_names: '',
  planned_enrollment: '',
});

const firstNonEmptyString = (...values) => {
  for (const value of values) {
    const trimmed = value != null ? String(value).trim() : '';
    if (trimmed) return trimmed;
  }
  return '';
};

/** Agent name(s) + dose level info for a regimen/cohort row (Trial Schema B). */
export const buildRegimenArmDescriptor = (source = {}, partFallback = {}) => {
  const agents = firstNonEmptyString(source.agent_names, partFallback.agent_names);
  const dose = firstNonEmptyString(
    source.dose_levels_frequency,
    source.regimen_dose_levels_frequency,
    partFallback.regimen_dose_levels_frequency
  );
  const regimenName = firstNonEmptyString(source.regimen);
  const core = agents || regimenName;
  if (!core && !dose) return '';
  if (core && dose) return `${core} — ${dose}`;
  return core || dose;
};

export const formatRegimenColumnHeader = (globalIndex, descriptor) => {
  const regimenNumber = parseInt(globalIndex, 10) || 0;
  const label = String(descriptor || '').trim();
  return label
    ? `Regimen ${regimenNumber} (${label})`
    : `Regimen ${regimenNumber} (Enter Name)`;
};

export const emptyPartConfig = () => ({
  part_name: '',
  phase: '',
  intervention_model: '',
  num_cohorts: 0,
  num_regimens: 0,
  num_agents: 0,
  regimen_dose_levels_frequency: '',
  agent_names: '',
  cohort_rows: [],
  direct_rows: [],
});

export const MAIN_STUDY_LABEL = 'Main Study';

export const parseDeclaredStudyPartCount = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

export const isMultiPartTrial = (declaredPartCount) =>
  parseDeclaredStudyPartCount(declaredPartCount) > 0;

export const effectiveStudyPartSlotCount = (declaredPartCount) =>
  isMultiPartTrial(declaredPartCount) ? parseDeclaredStudyPartCount(declaredPartCount) : 1;

export const hasConfiguredTrialPartCount = (studyOverview = {}) => {
  const n = studyOverview?.number_of_study_parts_new;
  return n !== undefined && n !== null && String(n).trim() !== '';
};

export const defaultTrialSchemaConfiguration = () => ({
  trial_types: {
    interventional: false,
    observational: false,
    expanded_access: false,
    patient_registry: false,
  },
  framework: {
    arm_structure: '',
    allocation: '',
    blinding: '',
    centre_configuration: '',
    geographic_distribution: '',
    total_sites: '',
    total_planned_participants: '',
    master_protocol_framework: '',
    adaptive_features: '',
    brief_description_of_adaptation_strategy: '',
  },
  parts: {},
  country_level: [{ country: '', description: '' }],
});

const TRIAL_TYPE_TO_DROPDOWN = {
  interventional: 'Interventional',
  observational: 'Observational',
  expanded_access: 'Expanded Access',
  patient_registry: 'Patient Registry',
};

/** Merge saved config with defaults (handles partial / legacy records). */
export const normalizeTrialSchemaConfiguration = (raw = {}) => {
  const base = defaultTrialSchemaConfiguration();
  const parts = { ...base.parts, ...(raw.parts || {}) };
  return {
    ...base,
    ...raw,
    trial_types: { ...base.trial_types, ...(raw.trial_types || {}) },
    framework: { ...base.framework, ...(raw.framework || {}) },
    parts,
    country_level:
      Array.isArray(raw.country_level) && raw.country_level.length > 0
        ? raw.country_level
        : base.country_level,
  };
};

/** Ensure part keys part1..partN exist when number of parts changes. 0 parts = one Main Study slot (part1). */
export const ensurePartSlots = (config, numParts) => {
  const declared = parseDeclaredStudyPartCount(numParts);
  const slotCount = effectiveStudyPartSlotCount(declared);
  const parts = { ...(config.parts || {}) };
  for (let i = 1; i <= slotCount; i++) {
    const key = `part${i}`;
    parts[key] = { ...emptyPartConfig(), ...(parts[key] || {}) };
  }
  Object.keys(parts).forEach((key) => {
    const num = parseInt(String(key).replace(/\D/g, ''), 10);
    if (num > slotCount) delete parts[key];
  });
  return { ...config, parts };
};

/**
 * Sync PDF trial schema config → legacy study_overview / study_treatments fields
 * used by dynamic tables, AI parse, and section generation.
 */
export const syncLegacyFieldsFromTrialSchema = (config, numPartsOverride) => {
  const cfg = normalizeTrialSchemaConfiguration(config);
  const declaredParts = parseDeclaredStudyPartCount(numPartsOverride);
  const slotCount = effectiveStudyPartSlotCount(declaredParts);
  const partKeys = Object.keys(cfg.parts || {})
    .filter((k) => /^part\d+$/i.test(k))
    .sort((a, b) => parseInt(a.replace(/\D/g, ''), 10) - parseInt(b.replace(/\D/g, ''), 10))
    .filter((k) => {
      const num = parseInt(String(k).replace(/\D/g, ''), 10);
      return num <= slotCount;
    });

  const numParts = declaredParts;

  const study_parts_part_names = {};
  const study_parts_cohorts = {};
  const study_parts_cohort_names = {};
  const regimens_per_part = {};
  let totalRegimens = 0;
  let totalAgents = 0;
  const regimen_arms = {};
  let globalRegimenIndex = 0;

  partKeys.forEach((partKey, partIndex) => {
    const part = cfg.parts[partKey] || emptyPartConfig();
    const partName = String(part.part_name || '').trim();
    if (partName) study_parts_part_names[partKey] = partName;

    const numCohorts = parseInt(part.num_cohorts) || 0;
    study_parts_cohorts[partKey] = numCohorts;

    totalAgents = Math.max(totalAgents, parseInt(part.num_agents) || 0);

    let numRegForPart = 0;
    if (numCohorts > 0) {
      numRegForPart = numCohorts;
      study_parts_cohort_names[partKey] = {};
      const cohortRows = Array.isArray(part.cohort_rows) ? part.cohort_rows : [];
      for (let cohortIdx = 0; cohortIdx < numCohorts; cohortIdx++) {
        const row = cohortRows[cohortIdx] || {};
        const cohortKey = `cohort${cohortIdx + 1}`;
        const name = String(row?.cohort_name || '').trim();
        if (name) study_parts_cohort_names[partKey][cohortKey] = name;

        globalRegimenIndex += 1;
        const descriptor = buildRegimenArmDescriptor(row, part);
        if (descriptor) regimen_arms[globalRegimenIndex] = descriptor;
      }
    } else {
      numRegForPart = parseInt(part.num_regimens) || 0;
      const directRows = Array.isArray(part.direct_rows) ? part.direct_rows : [];
      for (let r = 0; r < numRegForPart; r++) {
        globalRegimenIndex += 1;
        const row = directRows[r] || {};
        const descriptor = buildRegimenArmDescriptor(row, part);
        if (descriptor) regimen_arms[globalRegimenIndex] = descriptor;
      }
    }

    regimens_per_part[partKey] = numRegForPart;
    totalRegimens += numRegForPart;
  });

  const trialTypes = cfg.trial_types || {};
  const selectedTypes = Object.entries(trialTypes).filter(([, v]) => v);
  let study_type_dropdown = '';
  if (selectedTypes.length === 1) {
    study_type_dropdown = TRIAL_TYPE_TO_DROPDOWN[selectedTypes[0][0]] || '';
  } else if (selectedTypes.length > 1) {
    study_type_dropdown = selectedTypes
      .map(([k]) => TRIAL_TYPE_TO_DROPDOWN[k])
      .filter(Boolean)
      .join(', ');
  }

  const fw = cfg.framework || {};
  const study_design = {
    single_arm: fw.arm_structure === 'single_arm',
    multi_arm_randomized: fw.arm_structure === 'multi_arm' && fw.allocation === 'randomisation',
    multi_arm_non_randomized:
      fw.arm_structure === 'multi_arm' && fw.allocation && fw.allocation !== 'randomisation',
    crossover: partKeys.some((k) => cfg.parts[k]?.intervention_model === 'crossover'),
    multi_part_study: declaredParts > 1,
    single_part_multi_cohort:
      declaredParts <= 1 && partKeys.some((k) => (parseInt(cfg.parts[k]?.num_cohorts) || 0) > 1),
    multi_part_multi_cohort:
      declaredParts > 1 && partKeys.some((k) => (parseInt(cfg.parts[k]?.num_cohorts) || 0) > 1),
    platform_umbrella_basket: ['platform', 'umbrella', 'basket'].includes(fw.master_protocol_framework),
    adaptive: fw.adaptive_features === 'yes',
  };

  const firstPartPhase = partKeys.length ? cfg.parts[partKeys[0]]?.phase || '' : '';

  return {
    study_overview: {
      study_type_dropdown,
      study_phase: firstPartPhase,
      study_design,
      number_of_study_parts_new: numParts,
      study_parts_part_names,
      study_parts_cohorts,
      study_parts_cohort_names,
      trial_schema_configuration: cfg,
      trial_schema_cdisc_codes: resolveTrialSchemaCdiscCodes(cfg),
    },
    study_treatments: {
      num_regimens: totalRegimens,
      regimens_per_part,
      num_agents: totalAgents,
      regimen_arms,
    },
  };
};

/** Build trial schema config from legacy fields (load / migration). */
export const buildTrialSchemaFromLegacy = (studyOverview = {}, studyTreatments = {}) => {
  const existing = studyOverview.trial_schema_configuration;
  if (existing && Object.keys(existing.parts || {}).length > 0) {
    return normalizeTrialSchemaConfiguration(existing);
  }

  const config = defaultTrialSchemaConfiguration();
  const dropdown = String(studyOverview.study_type_dropdown || '').toLowerCase();
  if (dropdown.includes('interventional')) config.trial_types.interventional = true;
  if (dropdown.includes('observational')) config.trial_types.observational = true;
  if (dropdown.includes('expanded')) config.trial_types.expanded_access = true;
  if (dropdown.includes('registry')) config.trial_types.patient_registry = true;

  const sd = studyOverview.study_design || {};
  if (sd.single_arm) config.framework.arm_structure = 'single_arm';
  else if (sd.multi_arm_randomized || sd.multi_arm_non_randomized) config.framework.arm_structure = 'multi_arm';
  if (sd.multi_arm_randomized) config.framework.allocation = 'randomisation';
  if (sd.platform_umbrella_basket) config.framework.master_protocol_framework = 'platform';
  if (sd.adaptive) config.framework.adaptive_features = 'yes';

  const declaredParts = parseDeclaredStudyPartCount(studyOverview.number_of_study_parts_new);
  const slotCount = effectiveStudyPartSlotCount(declaredParts);
  for (let i = 1; i <= slotCount; i++) {
    const partKey = `part${i}`;
    const numCohorts = parseInt(studyOverview.study_parts_cohorts?.[partKey]) || 0;
    const numReg = parseInt(studyTreatments.regimens_per_part?.[partKey]) || 0;
    config.parts[partKey] = {
      ...emptyPartConfig(),
      part_name: studyOverview.study_parts_part_names?.[partKey] || '',
      phase: i === 1 ? studyOverview.study_phase || '' : '',
      num_cohorts: numCohorts,
      num_regimens: numReg || Math.floor((parseInt(studyTreatments.num_regimens) || 0) / Math.max(slotCount, 1)),
      num_agents: parseInt(studyTreatments.num_agents) || 0,
      agent_names: Object.values(studyTreatments.regimen_arms || {}).join('; '),
      intervention_model: sd.crossover ? 'crossover' : '',
    };
    if (numCohorts > 0) {
      config.parts[partKey].cohort_rows = Array.from({ length: numCohorts }, (_, idx) => {
        const cohortKey = `cohort${idx + 1}`;
        return {
          ...emptyCohortRow(),
          cohort_name: studyOverview.study_parts_cohort_names?.[partKey]?.[cohortKey] || '',
        };
      });
    }
  }

  return ensurePartSlots(config, declaredParts);
};
